import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserCog, Save } from "lucide-react";
import FeedbackButton from "@/components/FeedbackButton";

// A short list of common timezones — covers the floor-manager population we target.
const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "America/Anchorage", "America/Toronto", "America/Mexico_City",
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "Africa/Johannesburg", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo",
  "Australia/Sydney", "Pacific/Auckland", "UTC",
];

export default function ProfileEditDialog({ open, onClose, user, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", title: "", employee_id: "", timezone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: user?.name || "",
      phone: user?.phone || "",
      title: user?.title || "",
      employee_id: user?.employee_id || "",
      timezone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    });
  }, [open, user]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Display name is required");
    setBusy(true);
    try {
      const r = await api.patch("/users/me/profile", form);
      toast.success("Profile updated");
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl border-[#1a1a1a]/15">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <UserCog className="w-5 h-5 text-[#ff4500]" /> Edit your profile
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[#1a1a1a]/65">
            What appears next to your signature, in the sidebar, and on signed PDFs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Display name" required>
            <input data-testid="profile-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          </Field>
          <Field label="Email (sign-in identifier · not editable)">
            <input data-testid="profile-email" disabled value={user?.email || ""}
              className="w-full bg-[#f7f5f2] border border-[#1a1a1a]/10 rounded-xl px-3 py-2 text-sm text-[#1a1a1a]/55" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Job title">
              <input data-testid="profile-title" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Plant Manager"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
            </Field>
            <Field label="Phone">
              <input data-testid="profile-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 555 010 1234"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Employee ID (recorded in signatures)">
            <input data-testid="profile-employee-id" value={form.employee_id}
              onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              placeholder="EMP-12345 · badge # · payroll ID"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm font-mono" />
          </Field>
          <Field label="Timezone">
            <select data-testid="profile-timezone" value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
              {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>

          {/* Feedback link — used to be a global floating button, now lives on profile */}
          <div className="mt-2 border-t border-[#1a1a1a]/10 pt-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500]">// Help us improve</div>
              <div className="text-[12px] text-[#1a1a1a]/70">Spot a bug or have an idea? Tell us — it goes straight to the team.</div>
            </div>
            <FeedbackButton variant="inline" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="profile-cancel" onClick={onClose} className="btn-pill btn-pill-ghost">Cancel</button>
          <button data-testid="profile-save" onClick={save} disabled={busy} className="btn-pill btn-pill-primary disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save profile"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">
        {label} {required && <span className="text-[#ff4500]">*</span>}
      </label>
      {children}
    </div>
  );
}
